// src/utils/localStorageUtils.js
import * as Sentry from '@sentry/react';
import { isStorageBlocked } from './safeStorage';
/**
 * Utility for safely interacting with localStorage
 * Provides error-handled operations to prevent app crashes
 */

/**
 * Safely get an item from localStorage with error handling
 * @param {string} key - The key to get from localStorage
 * @param {*} defaultValue - Default value to return if retrieval fails
 * @returns {*} The parsed value or defaultValue if operation fails
 */
export const getItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    return JSON.parse(item);
  } catch (error) {
    // Blocked storage is the visitor's browser setting, not a fault (#623).
    if (!isStorageBlocked(error)) {
      Sentry.captureException(error, {
        tags: {
          component: 'localStorageUtils',
          action: 'getItem',
        },
        extra: { key },
      });
    }
    return defaultValue;
  }
};

/**
 * Safely set an item in localStorage with error handling
 * @param {string} key - The key to set in localStorage
 * @param {*} value - The value to store (will be JSON.stringified)
 * @returns {boolean} True if operation succeeded, false otherwise
 */
export const setItem = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    // Blocked storage is the visitor's browser setting, not a fault (#623).
    if (!isStorageBlocked(error)) {
      Sentry.captureException(error, {
        tags: {
          component: 'localStorageUtils',
          action: 'setItem',
        },
        extra: { key },
      });
    }
    return false;
  }
};

/**
 * Safely remove an item from localStorage with error handling
 * @param {string} key - The key to remove from localStorage
 * @returns {boolean} True if operation succeeded, false otherwise
 */
export const removeItem = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    // Blocked storage is the visitor's browser setting, not a fault (#623).
    if (!isStorageBlocked(error)) {
      Sentry.captureException(error, {
        tags: {
          component: 'localStorageUtils',
          action: 'removeItem',
        },
        extra: { key },
      });
    }
    return false;
  }
};

/**
 * Check if localStorage is available in the current environment
 * @returns {boolean} True if localStorage is available, false otherwise
 */
export const isLocalStorageAvailable = () => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};
